/**
 * Iter133 — Case → Field Ops tab
 *
 * Rebuilt to match the "LIVE FIELD SURVEILLANCE REPORT" spec exactly:
 *  - Live Control Strip (unchanged behaviour)
 *  - Two-column tactical layout:
 *      LEFT:  Surveillance Vehicle Log  →  Vehicle Information
 *             Live Event Timeline       →  LOG FIELD EVENT + Time/Activity table
 *      RIGHT: Subject Dossier & Activities → Subject Information + Activities table
 *             Residence & Location Profile → Location Information + Risk/Weak Spots
 *  - Dropdowns to swap between multiple Subjects / Vehicles / Locations
 *  - Photo = attached photo if present, else inline upload button
 *  - Assignment History pinned at the bottom
 *
 * Data loaded live from existing case APIs — no fake data.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../../hooks/use-toast';
import { BACKEND_URL } from '../../config/canonical';
import {
  MapPin, Play, Pause, Square, Loader2, Signal, Send, RefreshCw, Navigation2, User,
  Camera, Car, ShieldAlert, Target, Upload, Image as ImageIcon, Plus,
} from 'lucide-react';

const dtLocal = (v) => v ? new Date(v).toLocaleString() : '—';
const timeOnly = (v) => v ? new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

/* ---------- Panel scaffolding ---------- */
const PanelHeader = ({ title, right }) => (
  <div className="flex items-center justify-between bg-slate-800/70 border-b border-slate-700 px-3 py-2">
    <div className="text-[11px] font-mono uppercase tracking-wider text-slate-200 font-semibold">{title}</div>
    {right}
  </div>
);
const Panel = ({ title, right, children, testid }) => (
  <div className="rounded border border-slate-700 bg-[#0F172A]/70" data-testid={testid}>
    <PanelHeader title={title} right={right} />
    <div className="p-3">{children}</div>
  </div>
);
const SectionTitle = ({ children }) => (
  <div className="text-xs font-mono uppercase tracking-wider text-slate-300 font-bold mb-2">{children}</div>
);
const LabeledRow = ({ label, value, onChange, readOnly, placeholder }) => (
  <div className="grid grid-cols-[90px_1fr] items-center gap-2 py-1">
    <div className="text-[11px] text-slate-400 font-semibold">{label}:</div>
    {readOnly ? (
      <div className="text-sm text-white truncate">{value || '—'}</div>
    ) : (
      <Input value={value || ''} onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder || label}
        className="h-7 text-xs bg-slate-900 border-slate-700 text-white" />
    )}
  </div>
);

/* ---------- Photo tile ---------- */
function PhotoTile({ url, label, onUpload, testid }) {
  const inputRef = useRef(null);
  return (
    <div className="relative w-full aspect-[4/3] rounded border border-slate-700 bg-slate-900/60 overflow-hidden group"
         data-testid={testid}>
      {url ? (
        <img src={url} alt={label || 'photo'} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-slate-500 gap-2">
          <ImageIcon className="w-8 h-8" />
          <div className="text-[11px] uppercase tracking-wider">No photo</div>
        </div>
      )}
      {onUpload && (
        <>
          <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onUpload(f); }} />
          <button onClick={() => inputRef.current?.click()}
            className="absolute bottom-1 right-1 inline-flex items-center gap-1 bg-slate-900/80 hover:bg-slate-800 text-slate-100 text-[10px] px-2 py-1 rounded border border-slate-600 opacity-80 group-hover:opacity-100">
            <Upload className="w-3 h-3" /> {url ? 'Replace' : 'Upload'}
          </button>
        </>
      )}
    </div>
  );
}

/* ---------- Main component ---------- */
export default function FieldOpsTab({ caseId, onEndAssignment }) {
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [assignments, setAssignments] = useState([]);
  const [active, setActive] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [eventContent, setEventContent] = useState('');
  const [currentPos, setCurrentPos] = useState(null);

  // Live case entities
  const [subjects, setSubjects] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  const [subjectIdx, setSubjectIdx] = useState(0);
  const [vehicleIdx, setVehicleIdx] = useState(0);
  const [locationIdx, setLocationIdx] = useState(0);
  const [caseInfo, setCaseInfo] = useState(null);
  const [me, setMe] = useState(null);

  const watchIdRef = useRef(null);

  const loadCase = useCallback(async () => {
    try {
      const [sr, vr, lr, cr, mr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/cases/${caseId}/subjects`, { headers: H }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/vehicles`, { headers: H }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}/locations`, { headers: H }),
        fetch(`${BACKEND_URL}/api/cases/${caseId}`, { headers: H }),
        fetch(`${BACKEND_URL}/api/auth/me`, { headers: H }),
      ]);
      setSubjects(sr.ok ? await sr.json() : []);
      setVehicles(vr.ok ? await vr.json() : []);
      setLocations(lr.ok ? await lr.json() : []);
      setCaseInfo(cr.ok ? await cr.json() : null);
      setMe(mr.ok ? await mr.json() : null);
    } catch { /* no-op */ }
    // eslint-disable-next-line
  }, [caseId]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, ar] = await Promise.all([
        fetch(`${BACKEND_URL}/api/cases/${caseId}/field-assignments`, { headers: H }),
        fetch(`${BACKEND_URL}/api/field-assignments/me/active`, { headers: H }),
      ]);
      const list = lr.ok ? await lr.json() : [];
      const mine = ar.ok ? await ar.json() : null;
      setAssignments(list);
      const forThisCase = mine && mine.case_id === caseId ? mine : null;
      setActive(forThisCase);
      if (forThisCase) {
        const er = await fetch(`${BACKEND_URL}/api/field-assignments/${forThisCase.id}/events`, { headers: H });
        setEvents(er.ok ? await er.json() : []);
      } else {
        setEvents([]);
      }
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [caseId, token]);

  useEffect(() => { load(); loadCase(); }, [load, loadCase]);

  // Live GPS watch
  useEffect(() => {
    if (active && active.status === 'ACTIVE' && navigator.geolocation) {
      watchIdRef.current = navigator.geolocation.watchPosition(
        p => setCurrentPos({ lat: p.coords.latitude, lng: p.coords.longitude, at: Date.now() }),
        () => {}, { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
      );
      return () => {
        if (watchIdRef.current != null) navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      };
    }
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, [active]);

  const getPos = () => new Promise(resolve => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve({}), { enableHighAccuracy: true, timeout: 10000 },
    );
  });

  const start = async () => {
    setBusy(true);
    try {
      const pos = await getPos();
      const r = await fetch(`${BACKEND_URL}/api/field-assignments/start`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ case_id: caseId, purpose: 'SURVEILLANCE', ...pos }),
      });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Could not start assignment', description: d.detail || 'Error', variant: 'destructive' });
      toast({ title: 'Field assignment started', description: 'GPS capture is ON.' });
      await load();
    } finally { setBusy(false); }
  };

  const doAction = async (path, msg) => {
    if (!active) return;
    setBusy(true);
    try {
      let body = {};
      if (path === 'end') body = { ...(await getPos()) };
      const r = await fetch(`${BACKEND_URL}/api/field-assignments/${active.id}/${path}`, {
        method: 'POST', headers: JH, body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) toast({ title: 'Failed', description: d.detail || 'Error', variant: 'destructive' });
      else toast({ title: msg });
      if (path === 'end' && r.ok) {
        try { onEndAssignment && onEndAssignment(); } catch {}
      }
      await load();
    } finally { setBusy(false); }
  };

  const addEvent = async () => {
    if (!active) return;
    if (!eventContent.trim()) return toast({ title: 'Enter observation / activity', variant: 'destructive' });
    setBusy(true);
    try {
      const pos = await getPos();
      const r = await fetch(`${BACKEND_URL}/api/field-assignments/${active.id}/events`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ event_type: 'NOTE', content: eventContent, ...pos }),
      });
      const d = await r.json();
      if (!r.ok) toast({ title: 'Failed to add event', description: d.detail || 'Error', variant: 'destructive' });
      else { setEventContent(''); await load(); }
    } finally { setBusy(false); }
  };

  /* ---------- Photo uploads for case entities ---------- */
  const uploadEntityPhoto = async (kind, id, file) => {
    if (!id) return toast({ title: `Create a ${kind} first`, variant: 'destructive' });
    if (!file?.type?.startsWith('image/')) return toast({ title: 'Pick an image file', variant: 'destructive' });
    const fd = new FormData();
    fd.append('file', file);
    const r = await fetch(`${BACKEND_URL}/api/${kind}s/${id}/photo`, { method: 'POST', headers: H, body: fd });
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      return toast({ title: 'Upload failed', description: d.detail || 'Error', variant: 'destructive' });
    }
    toast({ title: 'Photo uploaded' });
    await loadCase();
  };

  /* ---------- Save edits back to entity ---------- */
  const patchEntity = async (kind, id, patch) => {
    if (!id) return;
    try {
      await fetch(`${BACKEND_URL}/api/${kind}s/${id}`, {
        method: 'PATCH', headers: JH, body: JSON.stringify(patch),
      });
    } catch { /* ignore */ }
  };

  /* ---------- Iter142: create new entity straight from Field Ops ---------- */
  const createVehicle = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/vehicles`, {
        method: 'POST', headers: JH, body: JSON.stringify({}),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast({ title: 'Could not add vehicle', description: d.detail || 'Error', variant: 'destructive' });
      await loadCase();
      // Auto-select the newly-created vehicle (appended at the end)
      setVehicleIdx(vehicles.length);
      toast({ title: 'Vehicle added', description: 'Fill in the details then press Save.' });
    } finally { setBusy(false); }
  };

  const createSubject = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/subjects`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ full_name: 'New Subject' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast({ title: 'Could not add subject', description: d.detail || 'Error', variant: 'destructive' });
      await loadCase();
      setSubjectIdx(subjects.length);
      toast({ title: 'Subject added', description: 'Fill in the details then press Save.' });
    } finally { setBusy(false); }
  };

  const createLocation = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/cases/${caseId}/locations`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ name: 'New Location', location_type: 'OTHER' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast({ title: 'Could not add location', description: d.detail || 'Error', variant: 'destructive' });
      await loadCase();
      setLocationIdx(locations.length);
      toast({ title: 'Location added', description: 'Fill in the details then press Save.' });
    } finally { setBusy(false); }
  };

  /* ---------- Live clock ---------- */
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (!active || active.status !== 'ACTIVE') return;
    const id = setInterval(() => setClockTick(t => t + 1), 1000);
    return () => clearInterval(id);
  }, [active]);
  const fmtElapsed = (startIso) => {
    if (!startIso) return '00:00:00';
    const start = new Date(startIso).getTime();
    const diff = Math.max(0, Math.floor((Date.now() - start) / 1000));
    const h = String(Math.floor(diff / 3600)).padStart(2, '0');
    const m = String(Math.floor((diff % 3600) / 60)).padStart(2, '0');
    const s = String(diff % 60).padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const subject = subjects[subjectIdx] || null;
  const vehicle = vehicles[vehicleIdx] || null;
  const location = locations[locationIdx] || null;

  /* ---------- UI ---------- */
  if (!active) {
    return (
      <div className="space-y-4" data-testid="case-field-ops-tab">
        <div className="rounded-lg border border-slate-700 bg-[#131A2A] p-6 text-center">
          <MapPin className="w-10 h-10 text-blue-400 mx-auto mb-2" />
          <h3 className="text-lg font-bold text-white">No Active Field Assignment</h3>
          <p className="text-sm text-slate-400 mt-1 mb-4">
            Start a surveillance assignment to open the Live Field Surveillance Report.
          </p>
          <Button className="bg-blue-600 hover:bg-blue-500 text-white" disabled={busy} onClick={start} data-testid="field-start-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Start Field Assignment
          </Button>
        </div>

        <AssignmentHistory assignments={assignments} />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="case-field-ops-tab">
      {/* Live Control Strip */}
      <div className="rounded-lg border border-emerald-500/40 bg-[#0B1830] p-3" data-testid="field-ops-live-strip">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <MapPin className="w-5 h-5 text-emerald-300" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400">FIELD OPERATIONS</div>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <span className="text-sm font-semibold text-white uppercase">
                  {active.status === 'ACTIVE' ? 'LIVE Assignment' : 'Assignment'}
                </span>
                <Badge className={active.status === 'ACTIVE'
                  ? 'bg-emerald-500/20 border border-emerald-500/50 text-emerald-300 text-[10px] font-mono'
                  : 'bg-amber-500/20 border border-amber-500/50 text-amber-300 text-[10px] font-mono'}>
                  {active.status}
                </Badge>
                {currentPos && (
                  <span className="inline-flex items-center gap-1 text-emerald-300 text-xs font-mono">
                    <Signal className="w-3 h-3" /> {currentPos.lat.toFixed(5)}, {currentPos.lng.toFixed(5)}
                  </span>
                )}
                <Badge className="bg-slate-800 border border-slate-600 text-slate-200 text-[10px] font-mono">
                  {(active.purpose || 'SURVEILLANCE').replace(/_/g, ' ')}
                </Badge>
                <span className="text-xs text-slate-400 font-mono uppercase">Elapsed Time:
                  <span className="ml-1 text-white tracking-wider" data-testid="field-ops-elapsed">
                    {fmtElapsed(active.start_time)}
                  </span>
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {active.status === 'ACTIVE' && (
              <Button className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" disabled={busy}
                onClick={() => doAction('pause', 'Paused')} data-testid="field-pause-btn">
                <Pause className="w-4 h-4 mr-1" /> Pause
              </Button>
            )}
            {active.status === 'PAUSED' && (
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" disabled={busy}
                onClick={() => doAction('resume', 'Resumed')} data-testid="field-resume-btn">
                <Play className="w-4 h-4 mr-1" /> Resume
              </Button>
            )}
            <Button className="bg-red-600 hover:bg-red-500 text-white font-semibold" disabled={busy}
              onClick={() => { if (window.confirm('Complete Field Assignment?')) doAction('end', 'Assignment ended'); }}
              data-testid="field-end-btn">
              <Square className="w-4 h-4 mr-1" /> End Assignment
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { load(); loadCase(); }} data-testid="field-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Report Title */}
      <div className="rounded-lg bg-[#0F172A] border border-slate-700 py-3 text-center">
        <h2 className="text-lg sm:text-xl font-bold text-white uppercase tracking-[0.2em]">
          Live Field Surveillance Report
        </h2>
      </div>

      {/* Two-column tactical report */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          <div>
            <SectionTitle>Surveillance Vehicle Log</SectionTitle>
            <Panel
              title="Vehicle Information"
              right={
                <div className="flex items-center gap-2">
                  {vehicles.length > 1 && (
                    <Select value={String(vehicleIdx)} onValueChange={v => setVehicleIdx(Number(v))}>
                      <SelectTrigger className="h-6 w-48 bg-slate-900 border-slate-700 text-xs text-white" data-testid="veh-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map((v, i) => (
                          <SelectItem key={v.id} value={String(i)}>
                            {[v.license_plate, v.make, v.model].filter(Boolean).join(' · ') || `Vehicle ${i + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" className="h-6 bg-blue-600 hover:bg-blue-500 text-white text-[11px] px-2"
                          onClick={createVehicle} disabled={busy} data-testid="veh-add-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Vehicle
                  </Button>
                </div>
              }
              testid="field-veh-panel"
            >
              {vehicles.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  No vehicles logged yet. Press <span className="text-white font-semibold">Add Vehicle</span> to create the first entry.
                </div>
              ) : (
              <div className="grid grid-cols-[180px_1fr] gap-3">
                <PhotoTile
                  url={vehicle?.photo_url}
                  label="Vehicle"
                  onUpload={vehicle?.id ? (f => uploadEntityPhoto('vehicle', vehicle.id, f)) : null}
                  testid="veh-photo"
                />
                <div>
                  <LabeledRow label="Plate" value={vehicle?.license_plate}
                    onChange={v => { setVehicles(prev => prev.map((x, i) => i === vehicleIdx ? { ...x, license_plate: v } : x)); }} />
                  <LabeledRow label="Make" value={vehicle?.make}
                    onChange={v => { setVehicles(prev => prev.map((x, i) => i === vehicleIdx ? { ...x, make: v } : x)); }} />
                  <LabeledRow label="Model" value={vehicle?.model}
                    onChange={v => { setVehicles(prev => prev.map((x, i) => i === vehicleIdx ? { ...x, model: v } : x)); }} />
                  <LabeledRow label="Color" value={vehicle?.color}
                    onChange={v => { setVehicles(prev => prev.map((x, i) => i === vehicleIdx ? { ...x, color: v } : x)); }} />
                  <LabeledRow label="Notes" value={vehicle?.notes}
                    onChange={v => { setVehicles(prev => prev.map((x, i) => i === vehicleIdx ? { ...x, notes: v } : x)); }} />
                  <div className="flex justify-end mt-1">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600 text-slate-200"
                      disabled={!vehicle?.id}
                      onClick={() => patchEntity('vehicle', vehicle?.id, {
                        license_plate: vehicle?.license_plate, make: vehicle?.make,
                        model: vehicle?.model, color: vehicle?.color, notes: vehicle?.notes,
                      }).then(() => toast({ title: 'Vehicle saved' }))}>
                      Save
                    </Button>
                  </div>
                </div>
              </div>
              )}
              {vehicles.length > 1 && (
                <div className="mt-2 bg-slate-900/50 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300">
                  <span className="font-semibold">Additional Known Vehicle:</span>{' '}
                  {vehicles
                    .filter((_, i) => i !== vehicleIdx)
                    .map(v => [v.color, v.make, v.model, v.license_plate].filter(Boolean).join(' '))
                    .join(' · ') || '—'}
                </div>
              )}
            </Panel>
          </div>

          <div>
            <SectionTitle>Live Event Timeline</SectionTitle>
            <Panel title="Live Investigation Event Log" testid="field-events-panel">
              <div className="mb-2">
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">Log Field Event</div>
                <div className="flex gap-2">
                  <Input value={eventContent} onChange={e => setEventContent(e.target.value)}
                    placeholder="Observation, action, note…"
                    className="h-8 text-xs bg-slate-900 border-slate-700 text-white"
                    onKeyDown={e => { if (e.key === 'Enter') addEvent(); }}
                    data-testid="field-event-content" />
                  <Button className="h-8 bg-blue-600 hover:bg-blue-500 text-white text-xs"
                    onClick={addEvent} disabled={busy} data-testid="field-add-event-btn">
                    <Send className="w-3 h-3 mr-1" /> Log
                  </Button>
                </div>
              </div>
              <div className="rounded border border-slate-700 overflow-hidden">
                <div className="grid grid-cols-[90px_1fr] bg-slate-800/70 text-[10px] font-mono uppercase tracking-wider text-slate-200 font-semibold">
                  <div className="px-2 py-1.5 border-r border-slate-700">Time</div>
                  <div className="px-2 py-1.5">Activity</div>
                </div>
                <div className="max-h-[320px] overflow-y-auto">
                  {events.length === 0 ? (
                    <div className="p-3 text-xs text-slate-500 italic text-center">No events logged yet.</div>
                  ) : events.map(e => (
                    <div key={e.id} className="grid grid-cols-[90px_1fr] text-xs border-t border-slate-800" data-testid={`field-event-${e.id}`}>
                      <div className="px-2 py-1.5 text-slate-400 font-mono border-r border-slate-800">
                        {timeOnly(e.timestamp)}
                      </div>
                      <div className="px-2 py-1.5 text-white">{e.content || e.event_type}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          <div>
            <SectionTitle>Subject Dossier &amp; Activities</SectionTitle>
            <Panel
              title="Subject Information"
              right={
                <div className="flex items-center gap-2">
                  {subjects.length > 1 && (
                    <Select value={String(subjectIdx)} onValueChange={v => setSubjectIdx(Number(v))}>
                      <SelectTrigger className="h-6 w-48 bg-slate-900 border-slate-700 text-xs text-white" data-testid="sub-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {subjects.map((s, i) => (
                          <SelectItem key={s.id} value={String(i)}>{s.full_name || `Subject ${i + 1}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" className="h-6 bg-blue-600 hover:bg-blue-500 text-white text-[11px] px-2"
                          onClick={createSubject} disabled={busy} data-testid="sub-add-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Subject
                  </Button>
                </div>
              }
              testid="field-sub-panel"
            >
              {subjects.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  No subjects on file. Press <span className="text-white font-semibold">Add Subject</span> to create one.
                </div>
              ) : (
              <>
              <div className="grid grid-cols-[140px_1fr] gap-3">
                <PhotoTile
                  url={subject?.photo_url}
                  label={subject?.full_name}
                  onUpload={subject?.id ? (f => uploadEntityPhoto('subject', subject.id, f)) : null}
                  testid="sub-photo"
                />
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <LabeledRow label="Name" value={subject?.full_name}
                    onChange={v => setSubjects(prev => prev.map((x, i) => i === subjectIdx ? { ...x, full_name: v } : x))} />
                  <LabeledRow label="DOB" value={subject?.date_of_birth}
                    onChange={v => setSubjects(prev => prev.map((x, i) => i === subjectIdx ? { ...x, date_of_birth: v } : x))} />
                  <LabeledRow label="Height" value={subject?.height}
                    onChange={v => setSubjects(prev => prev.map((x, i) => i === subjectIdx ? { ...x, height: v } : x))} />
                  <LabeledRow label="Weight" value={subject?.weight}
                    onChange={v => setSubjects(prev => prev.map((x, i) => i === subjectIdx ? { ...x, weight: v } : x))} />
                  <LabeledRow label="Special" value={subject?.scars_tattoos_marks}
                    onChange={v => setSubjects(prev => prev.map((x, i) => i === subjectIdx ? { ...x, scars_tattoos_marks: v } : x))} />
                  <LabeledRow label="Phone" value={(subject?.phone_numbers || [])[0] || ''}
                    onChange={v => setSubjects(prev => prev.map((x, i) => i === subjectIdx ? { ...x, phone_numbers: [v] } : x))} />
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-300 leading-relaxed">
                {subject?.physical_description || <span className="italic text-slate-500">No subject description recorded. Edit the fields above and press Save.</span>}
              </div>
              <div className="flex justify-end mt-2">
                <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600 text-slate-200"
                  disabled={!subject?.id}
                  onClick={() => patchEntity('subject', subject?.id, {
                    full_name: subject?.full_name, date_of_birth: subject?.date_of_birth,
                    height: subject?.height, weight: subject?.weight,
                    scars_tattoos_marks: subject?.scars_tattoos_marks,
                    phone_numbers: subject?.phone_numbers,
                  }).then(() => toast({ title: 'Subject saved' }))}>
                  Save
                </Button>
              </div>
              </>
              )}
            </Panel>

            <div className="mt-3">
              <Panel title="Subject Activities" testid="field-sub-activities">
                <div className="rounded border border-slate-700 overflow-hidden">
                  <div className="grid grid-cols-[1fr_1fr_90px_1.4fr] bg-slate-800/70 text-[10px] font-mono uppercase tracking-wider text-slate-200 font-semibold">
                    <div className="px-2 py-1.5 border-r border-slate-700">Subject</div>
                    <div className="px-2 py-1.5 border-r border-slate-700">Activity</div>
                    <div className="px-2 py-1.5 border-r border-slate-700">When</div>
                    <div className="px-2 py-1.5">Detail</div>
                  </div>
                  <div className="max-h-[220px] overflow-y-auto">
                    {events.filter(e => e.content || e.event_type).length === 0 ? (
                      <div className="p-3 text-xs text-slate-500 italic text-center">No subject activity logged yet.</div>
                    ) : events.map(e => (
                      <div key={`a-${e.id}`} className="grid grid-cols-[1fr_1fr_90px_1.4fr] text-xs border-t border-slate-800">
                        <div className="px-2 py-1.5 text-white border-r border-slate-800 truncate">{subject?.full_name || 'Subject'}</div>
                        <div className="px-2 py-1.5 text-slate-200 border-r border-slate-800 truncate">{e.event_type || 'NOTE'}</div>
                        <div className="px-2 py-1.5 text-slate-400 font-mono border-r border-slate-800">{timeOnly(e.timestamp)}</div>
                        <div className="px-2 py-1.5 text-white truncate">{e.content || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>
            </div>
          </div>

          <div>
            <SectionTitle>Residence &amp; Location Profile</SectionTitle>
            <Panel
              title="Location Information"
              right={
                <div className="flex items-center gap-2">
                  {locations.length > 1 && (
                    <Select value={String(locationIdx)} onValueChange={v => setLocationIdx(Number(v))}>
                      <SelectTrigger className="h-6 w-48 bg-slate-900 border-slate-700 text-xs text-white" data-testid="loc-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((l, i) => (
                          <SelectItem key={l.id} value={String(i)}>{l.name || l.address || `Location ${i + 1}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <Button size="sm" className="h-6 bg-blue-600 hover:bg-blue-500 text-white text-[11px] px-2"
                          onClick={createLocation} disabled={busy} data-testid="loc-add-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add Location
                  </Button>
                </div>
              }
              testid="field-loc-panel"
            >
              {locations.length === 0 ? (
                <div className="py-6 text-center text-xs text-slate-400">
                  No locations on file. Press <span className="text-white font-semibold">Add Location</span> to create one.
                </div>
              ) : (
              <>
              <div className="grid grid-cols-[1fr_180px] gap-3">
                <div>
                  <LabeledRow label="Address" value={[location?.address, location?.city, location?.state].filter(Boolean).join(', ')} readOnly />
                  <LabeledRow label="Est. Value" value={location?.estimated_value}
                    placeholder="e.g. $54,000"
                    onChange={v => setLocations(prev => prev.map((x, i) => i === locationIdx ? { ...x, estimated_value: v } : x))} />
                  <div className="mt-2">
                    <div className="text-[11px] text-slate-400 font-semibold mb-1">Property Description:</div>
                    <textarea
                      value={location?.property_description || location?.description || ''}
                      onChange={e => setLocations(prev => prev.map((x, i) => i === locationIdx ? { ...x, property_description: e.target.value } : x))}
                      rows={4}
                      placeholder="Describe the property, access points, and surroundings…"
                      className="w-full text-xs bg-slate-900 border border-slate-700 text-white rounded p-2" />
                  </div>
                  <div className="flex justify-end mt-2">
                    <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600 text-slate-200"
                      disabled={!location?.id}
                      onClick={() => patchEntity('location', location?.id, {
                        estimated_value: location?.estimated_value,
                        property_description: location?.property_description,
                      }).then(() => toast({ title: 'Location saved' }))}>
                      Save
                    </Button>
                  </div>
                </div>
                <PhotoTile
                  url={location?.photo_url}
                  label="Property"
                  onUpload={location?.id ? (f => uploadEntityPhoto('location', location.id, f)) : null}
                  testid="loc-photo"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mt-3">
                <Panel title="Location Risk" testid="loc-risk">
                  <LabeledRow label="Label" value={location?.risk_level}
                    onChange={v => setLocations(prev => prev.map((x, i) => i === locationIdx ? { ...x, risk_level: v } : x))} />
                  <LabeledRow label="Detail" value={location?.caution_indicators}
                    onChange={v => setLocations(prev => prev.map((x, i) => i === locationIdx ? { ...x, caution_indicators: v } : x))} />
                </Panel>
                <Panel title="Weak Spots" testid="loc-weak">
                  <LabeledRow label="Label" value={location?.weak_spot_label}
                    onChange={v => setLocations(prev => prev.map((x, i) => i === locationIdx ? { ...x, weak_spot_label: v } : x))} />
                  <LabeledRow label="Detail" value={location?.weak_spot_detail}
                    onChange={v => setLocations(prev => prev.map((x, i) => i === locationIdx ? { ...x, weak_spot_detail: v } : x))} />
                </Panel>
              </div>

              <div className="mt-3 flex items-center justify-end text-[11px] text-slate-400 font-mono">
                <div className="text-right">
                  <div className="text-white font-semibold">
                    {me?.name || me?.username || 'Investigator'} | {caseInfo?.case_number || caseId}
                  </div>
                  <div>
                    PI License # {me?.pi_license_number || 'A-00000'} · Page | 1
                  </div>
                </div>
              </div>
              </>
              )}
            </Panel>
          </div>
        </div>
      </div>

      <AssignmentHistory assignments={assignments} />
    </div>
  );
}

function AssignmentHistory({ assignments }) {
  return (
    <div className="rounded-lg border border-slate-700 bg-[#131A2A]">
      <div className="px-4 py-2 border-b border-slate-700 text-sm font-semibold text-white">Assignment History</div>
      {assignments.length === 0 ? (
        <div className="p-4 text-sm text-slate-500 text-center">No field assignments on this case yet.</div>
      ) : (
        <div className="divide-y divide-slate-800">
          {assignments.map(a => (
            <div key={a.id} className="px-4 py-2 text-sm flex items-center justify-between gap-2 flex-wrap" data-testid={`field-assignment-${a.id}`}>
              <div>
                <div className="text-white">
                  {a.purpose?.replace(/_/g, ' ')}
                  <Badge className={`ml-2 text-[10px] ${a.status === 'ENDED' ? 'bg-slate-700 text-slate-300' : 'bg-emerald-500/20 text-emerald-300'}`}>{a.status}</Badge>
                  {a.review_status && (
                    <Badge className={`ml-1 text-[10px] ${a.review_status === 'APPROVED' ? 'bg-blue-500/20 text-blue-300' : a.review_status === 'DENIED' ? 'bg-red-500/20 text-red-300' : 'bg-amber-500/20 text-amber-300'}`}>
                      {a.review_status}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-slate-400">
                  <User className="w-3 h-3 inline mr-0.5" /> {a.investigator_name} · {dtLocal(a.start_time)}
                  {a.end_time && ` → ${dtLocal(a.end_time)}`}
                </div>
              </div>
              {a.start_lat && a.start_lng && (
                <a href={`https://www.google.com/maps?q=${a.start_lat},${a.start_lng}`} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-blue-400 hover:underline">
                  <Navigation2 className="w-3 h-3 inline mr-0.5" /> {a.start_lat.toFixed(4)}, {a.start_lng.toFixed(4)}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
